import { useContext, useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Box, Grid, IconButton } from '@mui/material'

import { MenuIcon, TrackGoodIcon, TrackgoodIconSmall } from 'src/assets/icons'
import { AppVersion } from 'src/components'
import { Drawer } from './drawer-desktop.component'
import { ScanButton } from './scan-button.component'
import { NavbarItem } from './navbar-item.component'
import { useAccount, useCommonSettings, useMe, useUser } from 'src/hooks'
import { EAccountRoles, EUserRoles, INavBarData } from 'src/types'

import { ROUTES, STORAGE_KEYS } from 'src/constants'
import useStyles from './styles'
import { accountAdminRoutes, accountUserRoutes } from './utils'
import { joinAbsRoute } from 'src/common/utils'
import { LogoutButton } from './logout-button.component'
import { ThemeSwitchContext } from 'src/providers/theme-provider'
import { ScanToEarnButton } from './scan-to-earn-button.component'

interface INavbarDesktopProps {
  navbarData: INavBarData[]
}

const NavbarDesktop = ({ navbarData }: INavbarDesktopProps) => {
  const { data: account } = useAccount()
  const { setWhiteLable } = useContext(ThemeSwitchContext)

  const { data: user } = useUser()
  const { data: me, refetch } = useMe()
  const { data: settings } = useCommonSettings()

  const isSuperadmin = user?.roles.includes(EUserRoles.superadmin)

  const [isOpen, setIsOpen] = useState(!!localStorage.getItem(STORAGE_KEYS.NAVBAR_OPEN))

  const { accountId } = useParams()

  setWhiteLable(account && accountId ? account.white_label : undefined)

  const location = useLocation()
  const isAdmin = [EAccountRoles.admin, EAccountRoles.owner].some((role) =>
    account?.currentRoles.includes(role),
  )
  const isAccountUser = [
    EAccountRoles.admin,
    EAccountRoles.owner,
    // , EAccountRoles.user
  ].some((role) => account?.currentRoles.includes(role))

  const classes = useStyles({ isOpen })

  useEffect(() => {
    refetch()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const whiteLabelLogo =
    accountId && account?.white_label?.logo
      ? account.white_label.logo
      : me?.white_label
        ? me.white_label.logo
        : false

  const handleDrawer = () => {
    setIsOpen((toggle) => !toggle)
    isOpen
      ? localStorage.removeItem(STORAGE_KEYS.NAVBAR_OPEN)
      : localStorage.setItem(STORAGE_KEYS.NAVBAR_OPEN, 'open')
  }

  // const filteredNavbarData =
  //   isCustomer && me?.accounts && me.accounts.length < 1
  //     ? navbarData.filter((link) => link.showForCustomer)
  //     : navbarData

  const pathname = location.pathname.split('/')

  return (
    <Drawer variant="permanent" open={isOpen} PaperProps={{ className: classes.drawerPaper }}>
      <div className={classes.sidebar}>
        <Grid>
          <>
            <IconButton color="inherit" onClick={handleDrawer} className={classes.drawerButton}>
              <MenuIcon />
            </IconButton>
            <Grid>
              <Link
                to={
                  accountId
                    ? joinAbsRoute(ROUTES.ACCOUNTS, accountId, ROUTES.DASHBOARD)
                    : me?.accounts.length === 0
                      ? joinAbsRoute(ROUTES.USER, ROUTES.USER_DASHBOARD)
                      : joinAbsRoute(ROUTES.ACCOUNTS)
                }
              >
                {whiteLabelLogo ? (
                  <div className={classes.iconHolder}>
                    {/* <div
                      className={classes.iconWrapper}
                    style={{ backgroundImage: `url(${me.white_label.logo})` }}
                    >
                    </div> */}
                    <img src={whiteLabelLogo} alt="logo" className={classes.iconLogo} />
                  </div>
                ) : (
                  <>
                    <TrackGoodIcon className={classes.image} />
                    <TrackgoodIconSmall className={classes.imageSmall} />
                  </>
                )}
              </Link>
            </Grid>
            {!location.pathname.includes(ROUTES.USER) ? (
              <div>
                <ScanButton isOpen={isOpen} />
              </div>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ScanToEarnButton disabled={!settings?.scan_to_earn_enabled} isOpen={isOpen} />
              </Box>
            )}
            <div>
              {navbarData.map((link) => {
                let isSkip =
                  (!isAdmin && accountAdminRoutes.includes(link.path as ROUTES)) ||
                  (!isSuperadmin && link.path === '/' + ROUTES.ADMIN_ROUTES) ||
                  (!isSuperadmin && link.path === '/' + ROUTES.SCAN_TO_EARN_MANAGEMENT) ||
                  (!isAccountUser && accountUserRoutes.includes(link.path as ROUTES))

                const isScanHistory =
                  link.path === joinAbsRoute(ROUTES.SCAN_HISTORY) ||
                  (joinAbsRoute(ROUTES.SCAN_HISTORY).includes(link.path) && link.path !== '')

                const accountNavWithUserProfile = accountId
                  ? account?.isSingleAccount && !(isAdmin || isSuperadmin)
                  : true

                if (
                  location.pathname.includes(ROUTES.USER) &&
                  link.path !== '/' + ROUTES.ADMIN_ROUTES &&
                  link.path !== '/' + ROUTES.SCAN_TO_EARN_MANAGEMENT
                ) {
                  isSkip = false
                }

                if (link.path === ROUTES.PROFILE && !accountNavWithUserProfile) {
                  return null
                }

                const isScanToEarnDisabled =
                  !settings?.scan_to_earn_enabled &&
                  link.path === `/${ROUTES.USER}/${ROUTES.SCAN_TO_EARN}`

                return (
                  <NavbarItem
                    key={link.title}
                    title={link.title}
                    path={link.path}
                    icon={link.icon}
                    iconMode={link?.iconMode}
                    isActive={link.isActive}
                    isSkip={isSkip}
                    isScanHistory={isScanHistory}
                    isOpen={isOpen}
                    pathname={pathname}
                    withDivider={link.withDivider}
                    handleClick={link?.onClick}
                    disabled={link?.disabled || isScanToEarnDisabled}
                  />
                )
              })}
            </div>
          </>
        </Grid>
        <div className={classes.bottomContainer}>
          <LogoutButton isOpen={isOpen} />
          <div className={classes.versionWrapper}>
            <AppVersion />
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export { NavbarDesktop }
